import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for fade-in and slide-up animations
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection Observer threshold (default: 0.1)
 * @param {boolean} options.triggerOnMount - Whether to trigger animation on mount (default: true)
 * @param {boolean} options.triggerOnScroll - Whether to trigger animation on scroll (default: true)
 * @returns {Object} - { ref, isVisible }
 */
export const useFadeInAnimation = ({
  threshold = 0.1,
  triggerOnMount = true,
  triggerOnScroll = true,
} = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const currentElement = elementRef.current;

    if (!currentElement) return;

    // Trigger animation on mount if enabled
    if (triggerOnMount) {
      setIsVisible(true);
    }

    // Set up Intersection Observer for scroll animations
    if (triggerOnScroll) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
            }
          });
        },
        {
          threshold,
          rootMargin: "0px",
        }
      );

      // Observe the element
      observer.observe(currentElement);

      // Also check immediately if element is already in viewport
      // This handles the case where element is visible on initial load
      const checkInitialVisibility = () => {
        const rect = currentElement.getBoundingClientRect();
        const isVisibleNow =
          rect.top < window.innerHeight &&
          rect.bottom > 0 &&
          rect.left < window.innerWidth &&
          rect.right > 0;

        if (isVisibleNow && !triggerOnMount) {
          setIsVisible(true);
        }
      };

      // Check after a small delay to ensure layout is complete
      const timeoutId = setTimeout(checkInitialVisibility, 100);

      return () => {
        clearTimeout(timeoutId);
        observer.unobserve(currentElement);
      };
    }
  }, [threshold, triggerOnMount, triggerOnScroll]);

  return { ref: elementRef, isVisible };
};
